import React from "react";
import { graphql } from "gatsby";
import {
  Modal,
  Box,
  Fade,
  ModalProps,
  makeStyles,
  useTheme,
  useMediaQuery,
  styled
} from "@material-ui/core";
import Stories from "react-insta-stories";
import ModalCloseButton from "../../components/basic-modal/ModalCloseButton";
import useXSmallHeightMediaQuery from "../../hooks/use-xsmall-height-mq";
import { convertDatoCmsAssetUrlToCloudfrontUrl } from "../../helpers/cdn";
import { analyticsTrackEvent } from "../../services/analytics";

type Props = Omit<ModalProps, "children"> & {
  story: Queries.StoryFragmentFragment;
};

const useStyles = makeStyles(theme => ({
  modal: {
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    backdropFilter: `blur(8px)`,
    backgroundColor: `rgba(0, 0, 0, 0.4)`,
    "& .modal-close-button": {
      position: "absolute",
      top: theme.spacing(2),
      right: theme.spacing(1),
      color: theme.palette.common.white,
      zIndex: 1000,
      "&:hover": {
        backgroundColor: "#646360"
      },
      [theme.breakpoints.up("md")]: {
        top: theme.spacing(3),
        right: theme.spacing(3)
      }
    }
  }
}));

const StoryCustomStyles = styled("div")(({ theme }) => ({
  // react-insta-stories doesnt have classNames so we have to select the div manually...
  "& > div:first-child > div:nth-child(2)": {
    justifyContent: "center"
  },
  [theme.breakpoints.up("md")]: {
    "& > div:first-child": {
      borderRadius: "8px",
      overflow: "hidden"
    }
  }
}));

export default function StoryModal({ story, ...modalProps }: Props) {
  const classes = useStyles();
  const theme = useTheme();
  const isDesktop = useMediaQuery(theme.breakpoints.up("md"));

  if (!story.slides || story.slides.length === 0) {
    return null;
  }

  // Documentation: https://github.com/mohitk05/react-insta-stories#readme
  const storiesData = story.slides.map(slide => {
    return {
      url: convertDatoCmsAssetUrlToCloudfrontUrl(slide.video.url),
      type: "video"
    };
  });

  // Calculate aspect ratio based on first slide width & height
  const width = story.slides[0].video.width;
  const height = story.slides[0].video.height;
  const aspectRatio = width / height;

  const handleStoryStart = index => {
    analyticsTrackEvent(`story.slide-${index + 1}.viewed`);
  };

  return (
    <Modal className={classes.modal} {...modalProps}>
      <Fade in={modalProps.open} style={{ outline: 0 }}>
        <Box>
          {/* Close button */}
          {modalProps.onClose && (
            <ModalCloseButton
              variant="dark"
              onClick={e => modalProps.onClose(e, "escapeKeyDown")}
            />
          )}

          {/* Desktop Prev/Next buttons */}

          {/* react-insta-stories */}
          <StoryCustomStyles>
            <Stories
              key={isDesktop ? "desktop" : "mobile"}
              stories={storiesData}
              onAllStoriesEnd={modalProps?.onClose}
              onStoryStart={handleStoryStart}
              width={
                isDesktop
                  ? `min(calc((100vh - 64px) * ${aspectRatio}), ${width}px)`
                  : "100vw"
              }
              height={
                isDesktop
                  ? `min(calc(100vh - 64px), ${height}px)`
                  : "var(--100vh)"
              }
              keyboardNavigation
            />
          </StoryCustomStyles>
        </Box>
      </Fade>
    </Modal>
  );
}

export const StoryFragment = graphql`
  fragment StoryFragment on DatoCmsStory {
    slides {
      video {
        url
        width
        height
      }
    }
    model {
      apiKey
    }
  }
`;
